import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { FavoriteProjectsStoreFacade } from '@app/projects-module/state/favorite-projects/favorite-projects-store.facade'
import { UpdateFavoriteProjectSortCommand } from '@app/projects-module/core/commands/update-favorite-project-sort.command'
import { of } from 'rxjs'
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators'
import {
  addFavoriteProject,
  addFavoriteProjectStore,
  addFavoriteProjectSuccess,
  setFavoriteProjects,
  removeFavoriteProject,
  removeFavoriteProjectStore,
  removeFavoriteProjectSuccess,
  updateProjectFavoritePosition,
  updateProjectFavoritePositionStore,
  updateProjectFavoritePositionSuccessfully,
  loadFavoriteProjects,
  loadFavoriteProjectsError,
} from '@app/projects-module/state/favorite-projects/favorite-projects.actions'
import { AddFavoriteProjectCommand } from '@app/projects-module/core/commands/add-favorite-project.command'
import { CurrentOrganization } from '@app/shared/services/current-organization'
import { RemoveFavoriteProjectCommand } from '@app/projects-module/core/commands/remove-favorite-project.command'
import { GetFavoriteProjectsQuery } from '@app/projects-module/core/queries/get-favorite-projects.query'
import { ToasterService } from '@app/shared/services/toaster'
import { HttpErrorResponse } from '@angular/common/http'

@Injectable()
export class FavoriteProjectsEffects {
  constructor(
    private actions$: Actions,
    private currentOrganization: CurrentOrganization,
    private favoriteProjectsStore: FavoriteProjectsStoreFacade,
    private toaster: ToasterService,
    private addFavoriteProjectCommand: AddFavoriteProjectCommand,
    private removeFavoriteProjectCommand: RemoveFavoriteProjectCommand,
    private updateFavoriteProjectSortCommand: UpdateFavoriteProjectSortCommand,
    private getFavoriteProjectsQuery: GetFavoriteProjectsQuery
  ) {}

  sortFavoriteProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProjectFavoritePosition),
      switchMap((action) => {
        const ids = action.newOrder.map((el) => el.projectId)
        return this.updateFavoriteProjectSortCommand.call(action.organizationId, ids).pipe(
          switchMap(() => of(updateProjectFavoritePositionSuccessfully({ organizationId: action.organizationId }))),
          catchError((error: HttpErrorResponse) => {
            this.toaster.errorHTTP(error)
            return of(updateProjectFavoritePositionStore({ newOrder: action.oldOrder }))
          })
        )
      })
    )
  )

  loadFavoriteProjectsAfterSort = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProjectFavoritePositionSuccessfully),
      switchMap((action) => {
        return of(loadFavoriteProjects({ organizationId: action.organizationId }))
      })
    )
  )

  getFavoriteProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFavoriteProjects),
      switchMap((action) => {
        return this.getFavoriteProjectsQuery.call(action.organizationId).pipe(
          switchMap((res) => of(setFavoriteProjects({ favoriteProjects: res.data }))),
          catchError((error: HttpErrorResponse) => {
            this.toaster.errorHTTP(error)
            return of(loadFavoriteProjectsError({ error }))
          })
        )
      })
    )
  )

  addFavoriteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFavoriteProject),
      withLatestFrom(this.favoriteProjectsStore.getFavoriteProjects()),
      switchMap(([{ project }, favoriteProjects]) => {
        const favoriteProjectId = favoriteProjects.find((p) => p.project_id == project.id)!.id
        return this.addFavoriteProjectCommand.call(this.currentOrganization.id, project.id).pipe(
          switchMap(() => of(addFavoriteProjectSuccess())),
          catchError((error: HttpErrorResponse) => {
            this.toaster.errorHTTP(error)
            return of(removeFavoriteProjectStore({ favoriteProjectId, project }))
          })
        )
      })
    )
  )

  removeFavoriteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeFavoriteProject),
      switchMap(({ favoriteProjectId, project }) => {
        return this.removeFavoriteProjectCommand.call(this.currentOrganization.id, project.id).pipe(
          switchMap(() => {
            return of(removeFavoriteProjectSuccess())
          }),
          catchError((error: HttpErrorResponse) => {
            this.toaster.errorHTTP(error)
            return of(addFavoriteProjectStore({ project }))
          })
        )
      })
    )
  )
}
